export interface ISignerState {
  envelope?: IEnvelope;
  zoom: number;
  signatureText: string;
  signaturePendingPage?: number;
  signaturePendingIndex?: number;
  initials: string;
  showSignatureModal: boolean;
  unsignedBlocksByPage: Array<Array<IBlock>>;
  signedBlocksByPage: Array<Array<IBlock>>;
  isSubmitting: boolean;
  alreadySigned: boolean;
  termsAccepted: boolean;
  showExitSigningModal: boolean;
}

export interface IConfig {
  areLegalLinksVisible: boolean;
  senderUi: {
    sendRedirectUrl: string;
  };
  eSignAgreementUrl: string;
  dateFormat: DateFormatType;
}

export enum DateFormatType {
  Default = 'MM/DD/YYYY',
  DayMonthYear = 'DD/MM/YYYY',
  YearMonthDay = 'YYYY/MM/DD',
}

export interface IEnvelope {
  envelopeId: string;
  documents: IDocument[];
  signer: ISigner;
  expiration: string;
  status: string;
  config: IConfig;
  metadata: {
    ownerRegionCountries: string;
    ownerSubscriberId: string;
    fileCity?: string;
    fileZip?: string;
    collectAllSignerPhoneNumbers?: boolean;
  };
  pageDimensions: {
    height: number;
    width: number;
  };
}

export interface IThumbnail {
  page: number;
  width: number;
  url: string;
}

export interface IPageDimension {
  height: number;
  width: number;
}

export interface IDocument {
  documentId: string;
  fileName: string;
  url: string;
  numberOfPages: number;
  pageThumbnails: IThumbnail[];
  blocks: IBlock[];
  blockGroups: IGroup[];
  pages: IPageDimension[];
  order: number;
  metadata: StringDictionary;
}
export interface IBlock {
  blockId: string;
  pageNumber: number;
  documentId: string;
  blockType: string;
  status: string;
  x: number;
  y: number;
  value?: string | boolean | null;
  width?: number;
  height?: number;
  required?: boolean;
  fontType?: string;
  fontSize?: number;
  groupId?: string;
  signed?: boolean;
  readyOnly?: boolean;
}

export interface IGroup {
  type: string;
  id: string;
  documentId: string;
  envelopeId: string;
  pageNumber: number;
  blockIds: string[];
  validation: {
    rule: 'gte' | 'lte' | 'eq' | 'bt';
    value: number;
    valueB?: number;
  };
  readyOnly?: boolean;
}

export type ContactType =
  | 'Buyer'
  | 'Seller'
  | 'TitleOfficer'
  | 'EscrowOfficer'
  | 'LoanOfficer'
  | 'BuyerAgent'
  | 'SellerAgent'
  | 'Other'
  | 'BuyersLawyer'
  | 'SellersLawyer'
  | 'Broker';

export interface ISigner {
  signerId: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  signingStatus: string;
  executedDocuments: Array<IExecutedDocument>;
  dateSigned: string;
  title?: string;
  signingSettings?: ISignatureSettings;
  oktaUserId?: boolean;
  metadata?: {
    // This gets set in files-api. See: https://github.com/skyslope/files-api/blob/991e78d7250a98cbd8efaf492963778bb379454b/src/SkySlope.Forms.Files.FileService/Infrastructure/Services/DigiSignService/DigiSign3Service.cs#L169
    audienceContactType?: ContactType;
  };
}

export interface IExecutedDocument {
  url: string;
  documentId: string;
  fileName: string;
  order: number;
}

export interface ISignatureSettings {
  signatureValue?: string;
  initialsValue?: string;
  signatureFont?: string;
  signatureType?: 'text' | 'image';
  saveSigningSettings?: boolean;
  initialsImageBase64?: string;
  signatureImageBase64?: string;
  signatureText?: string;
  initialsText?: string;
}

interface StringDictionary {
  [key: string]: string;
}
